import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Megaphone,
  MapPin,
  Factory,
  Sun,
  Droplets,
  ShieldCheck,
  Ruler,
  Wrench,
  Star,
  Sparkles,
} from 'lucide-react';
import './LedVisionSub.css';

const HERO_IMG =
  'https://customer-assets-gfyr7b9c.emergentagent.net/job_security-display-hub/artifacts/dkxai0bs_HBE-HBM_%E5%AE%A4%E5%86%85_%E7%94%BB%E5%83%8F%E3%81%AE%E3%81%BF.png';
const IMG_HBE_HBM = HERO_IMG;

const SeriesBlock = ({ s }) => {
  const Icon = s.Icon;
  return (
    <div className="lvs-series-detail" data-testid={`series-${s.name}`}>
      <div className="lvs-detail-top">
        {s.hasImage ? (
          <div className="lvs-detail-image"><img src={s.image} alt={s.name} /></div>
        ) : (
          <div className="lvs-detail-image-gradient" aria-hidden="true">
            <span className="lvs-detail-gradient-icon"><Icon size={44} strokeWidth={1.4} /></span>
          </div>
        )}
        <div className="lvs-detail-body">
          <span className="lvs-detail-name-tag">{s.name}</span>
          <h3 className="lvs-detail-title">{s.title}</h3>
          <p className="lvs-detail-tagline">{s.tagline}</p>
          <p className="lvs-detail-desc">{s.desc}</p>
        </div>
      </div>
      <div className="lvs-detail-features">
        <div className="lvs-feature-col">
          <div className="lvs-feature-head">
            <span><MapPin size={16} strokeWidth={1.7} /></span>
            用途 <small>/ Use</small>
          </div>
          <ul className="lvs-feature-list">
            {s.uses.map((u, k) => <li key={k}>{u}</li>)}
          </ul>
        </div>
        <div className="lvs-feature-col">
          <div className="lvs-feature-head">
            <span><Star size={16} strokeWidth={1.7} /></span>
            特長 <small>/ Features</small>
          </div>
          <ul className="lvs-feature-list">
            {s.features.map((f, k) => <li key={k}>{f}</li>)}
          </ul>
        </div>
      </div>
      <div className="lvs-spec-title">製品概要 / SPECIFICATIONS</div>
      <table className="lvs-spec-table">
        <tbody>
          {s.specs.map((row, k) => (
            <tr key={k}><th>{row[0]}</th><td>{row[1]}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OutdoorLedVision = () => {
  const useCases = [
    { Icon: Megaphone, label: '屋外看板' },
    { Icon: Building2, label: '建物外壁・ファサード' },
    { Icon: MapPin, label: '駅前広告' },
    { Icon: Building2, label: '商業施設' },
    { Icon: Factory, label: '工場・施設案内' },
    { Icon: Sun, label: '高輝度表示' },
    { Icon: Droplets, label: '防水・防塵' },
    { Icon: ShieldCheck, label: '耐候性' },
  ];

  const series = [
    {
      name: 'HLE / HLE-mini',
      title: 'HLE / HLE-mini シリーズ',
      tagline: '高輝度で視認性に優れた屋外対応モデル',
      desc: '屋外イベント・コンサート・屋外表示など、直射日光下での視認性が求められるシーンに対応する高輝度モデルです。設置性と耐候性を両立し、幅広い屋外用途にご使用いただけます。',
      hasImage: false,
      Icon: Sun,
      uses: ['屋外イベント', 'コンサート・ステージ', '屋外広告表示', '施設外壁演出'],
      features: ['高輝度（屋外視認性）', '防水・防塵仕様', '軽量設計', '設置柔軟性', '耐候性の高いキャビネット'],
      specs: [
        ['主な用途', '屋外イベント / コンサート / 屋外広告 / 屋外表示'],
        ['ピクセルピッチ', '3.91 / 4.81 mm'],
        ['タイプ', '屋外高輝度モデル（HLE / 小型モデルHLE-mini）'],
        ['特長', '高輝度・高視認性 / 防水防塵 / 軽量・省施工 / 屋外常設対応'],
      ],
    },
    {
      name: 'HBE / HBM',
      title: 'HBE / HBM シリーズ',
      tagline: '常設屋外案件にも対応しやすいシリーズ',
      desc: '屋外広告・ファサード・大型表示など、常設用途を想定した屋外向けシリーズです。幅広いピクセルピッチのラインナップにより、看板規模から大型施設案内まで柔軟に対応します。',
      image: IMG_HBE_HBM,
      hasImage: true,
      uses: ['屋外広告', 'ファサード', '大型表示', '駅前・商業施設案内'],
      features: ['幅広いピクセルピッチ選択（2.6〜4.81mm）', '常設向けの筐体設計', '高輝度・高コントラスト', '長期運用に配慮した設計'],
      specs: [
        ['主な用途', '屋外広告 / ファサード / 大型表示 / 駅前案内'],
        ['ピクセルピッチ', '2.6 / 2.97 / 3.91 / 4.81 mm'],
        ['タイプ', '屋外常設向け（HBE / HBM）'],
        ['特長', '常設対応 / 幅広いピッチ選択 / 屋外環境対応'],
      ],
    },
  ];

  const points = [
    { Icon: Sun, title: '輝度と視認性', body: '直射日光下でも見えやすい輝度を確保します。周辺環境の明るさを踏まえて選定します。' },
    { Icon: Droplets, title: '防水・防塵性能', body: '設置環境に応じたIP等級の筐体を選び、雨・砂塵・湿気への耐性を確保します。' },
    { Icon: Ruler, title: '視認距離とサイズ', body: '見る距離に合わせてピクセルピッチと画面サイズを選定し、視認性を確保します。' },
    { Icon: Wrench, title: '取付・メンテナンス', body: '足場・電源・配線経路・保守方法を事前に確認し、長期運用しやすい構成をご提案します。' },
  ];

  return (
    <div className="lvs-page" data-testid="led-vision-outdoor-page">
      <section className="lvs-hero" data-testid="outdoor-hero">
        <div className="lvs-hero-bg" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
        <div className="lvs-hero-overlay" aria-hidden="true" />
        <div className="lvs-hero-inner">
          <div className="lvs-hero-content">
            <Link to="/products/led-vision" className="lvs-back" data-testid="back-to-led-vision">
              <ArrowLeft size={14} /> LEDビジョン一覧へ戻る
            </Link>
            <span className="lvs-hero-eyebrow">OUTDOOR LED VISION</span>
            <h1 className="lvs-hero-title">屋外LEDビジョン</h1>
            <p className="lvs-hero-sub">屋外の視認性と耐候性を、確かに。</p>
            <p className="lvs-hero-desc">
              屋外広告・建物外壁・駅前広告・工場案内など、高輝度と耐候性が求められる場所に適したLEDビジョンです。設置条件やコンテンツに応じて、最適な仕様をご提案します。
            </p>
            <div className="lvs-hero-actions">
              <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
              <a href="#series" className="lvs-btn lvs-btn-outline">取扱シリーズを見る <ArrowRight size={18} /></a>
            </div>
          </div>
        </div>
      </section>

      <section className="lvs-section">
        <div className="lvs-container">
          <div className="lvs-two-col">
            <div className="lvs-intro-text">
              <span className="lvs-eyebrow">ABOUT</span>
              <h2 className="lvs-h2">屋外LEDビジョンとは</h2>
              <p>屋外LEDビジョンは、屋外環境での使用を前提とした高輝度・防水・防塵仕様のディスプレイです。直射日光下でも視認性を確保でき、雨風や気温差にも耐える設計で、屋外広告・建物外壁・駅前などで長期的にご使用いただけます。</p>
            </div>
            <div className="lvs-intro-visual" aria-hidden="true"><img src={HERO_IMG} alt="" /></div>
          </div>
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">USE CASES</span>
            <h2 className="lvs-h2">主な用途</h2>
            <p className="lvs-lead">屋外の広告・案内・情報表示など、視認性と耐久性が求められるシーンで活躍します。</p>
          </div>
          <div className="lvs-use-grid">
            {useCases.map((u, i) => { const Icon = u.Icon;
              return (
                <div key={i} className="lvs-use-item" data-testid={`use-${i}`}>
                  <span className="lvs-use-icon"><Icon size={18} strokeWidth={1.7} /></span>
                  <span className="lvs-use-label">{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-section" id="series">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">PRODUCT SERIES</span>
            <h2 className="lvs-h2">取扱シリーズ</h2>
            <p className="lvs-lead">屋外用途に合わせて、2つのシリーズをご用意しています。</p>
          </div>
          {series.map((s, i) => <SeriesBlock key={i} s={s} />)}
        </div>
      </section>

      <section className="lvs-section lvs-section-gray">
        <div className="lvs-container">
          <div className="lvs-head">
            <span className="lvs-eyebrow">SELECTION</span>
            <h2 className="lvs-h2">選定ポイント</h2>
            <p className="lvs-lead">現場の条件と運用方法を確認し、長期使用に向いた構成をご提案します。</p>
          </div>
          <div className="lvs-points">
            {points.map((p, i) => { const Icon = p.Icon;
              return (
                <div key={i} className="lvs-point-card" data-testid={`point-${i}`}>
                  <span className="lvs-point-icon"><Icon size={20} strokeWidth={1.7} /></span>
                  <h3 className="lvs-point-title">{p.title}</h3>
                  <p className="lvs-point-body">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lvs-cta" data-testid="cta">
        <div className="lvs-cta-inner">
          <div className="lvs-cta-text">
            <h2>屋外LEDビジョンのご相談は、株式会社エイチエスへ。</h2>
            <p>設置場所・用途・ご予算に合わせて、最適なシリーズと構成をご提案いたします。</p>
          </div>
          <div className="lvs-cta-actions">
            <Link to="/contact" className="lvs-btn lvs-btn-primary">お問い合わせ <ArrowRight size={18} /></Link>
            <Link to="/products/led-vision" className="lvs-btn lvs-btn-outline"><ArrowLeft size={16} /> LEDビジョン一覧</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OutdoorLedVision;
